import React, { useEffect } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'

import { BarChart3, FileCheck, Lock, Shield, Users } from 'lucide-react'

import eMozartLogo from '@/assets/emozart_logo.png'
import { useAuth } from '@/hooks/useAuth'

/**
 * Layout para páginas de autenticación (login, registro, recuperación de contraseña)
 */
export const AuthLayout: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  // Obtener la ruta de redirección desde el state (si existe)
  // const from = (location.state as any)?.from || '/dashboard'
  type LocationState = { from?: string }
  const state = location.state as LocationState | null
  const from = state?.from || '/dashboard'

  // Si ya está autenticado, redirigir
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      // Pequeño delay para evitar flash de contenido
      const timer = setTimeout(() => {
        window.location.href = from
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isAuthenticated, isLoading, from])

  // Mostrar loading mientras verifica autenticación
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  // Si está autenticado, redirigir (como fallback)
  if (isAuthenticated) {
    return <Navigate to={from} replace />
  }

  return (
    <div className="min-h-screen flex">
      {/* Panel izquierdo - Formulario */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-2">
              <img src={eMozartLogo} alt="eMozart" className="h-16 w-auto" />
            </div>
            <p className="mt-2 text-md text-gray-600">Plataforma de Assessment de Ciberseguridad</p>
          </div>

          {/* Outlet para el contenido específico (LoginPage, etc.) */}
          <Outlet />

          {/* Footer */}
          <div className="mt-8">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Basado en NIST CSF 2.0</span>
              </div>
            </div>

            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">
                © {new Date().getFullYear()} eMozart. Todos los derechos reservados.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Panel derecho - Hero/Información */}
      <div className="hidden lg:block relative w-0 flex-1">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-800">
          {/* Patrón de fondo */}
          <div className="absolute inset-0 bg-black opacity-10">
            <svg className="absolute inset-0 h-full w-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path
                    d="M 40 0 L 0 0 0 40"
                    fill="none"
                    stroke="white"
                    strokeWidth="1"
                    opacity="0.2"
                  />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>

          {/* Contenido */}
          <div className="relative h-full flex flex-col justify-center items-center p-12">
            <div className="max-w-lg text-center">
              <h2 className="text-4xl font-bold text-white mb-6">
                Evalúa la madurez en ciberseguridad de tu organización
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                Utiliza el framework NIST CSF 2.0 para identificar brechas y generar planes de
                acción efectivos.
              </p>

              {/* Features */}
              <div className="grid grid-cols-2 gap-4 mt-12">
                <Feature
                  icon={FileCheck}
                  title="Evaluación Completa"
                  description="138 subcategorías evaluadas"
                />
                <Feature
                  icon={BarChart3}
                  title="Análisis de Brechas"
                  description="Identifica áreas de mejora"
                />
                <Feature icon={Users} title="Colaboración" description="Trabaja con tu equipo" />
                <Feature icon={Lock} title="Seguro" description="Datos protegidos" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Componente de característica para el panel derecho
 */
const Feature: React.FC<{
  icon: React.ElementType
  title: string
  description: string
}> = ({ icon: Icon, title, description }) => (
  <div className="text-left">
    <div className="flex items-center space-x-3 mb-2">
      <div className="flex-shrink-0">
        <div className="flex items-center justify-center h-10 w-10 rounded-md bg-white bg-opacity-20">
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
      <h3 className="text-lg font-semibold text-white">{title}</h3>
    </div>
    <p className="text-sm text-blue-100 ml-13">{description}</p>
  </div>
)

/**
 * Variante minimalista del AuthLayout (opcional)
 */
export const AuthLayoutMinimal: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()
  // Obtener la ruta de redirección desde el state (si existe)
  // const from = (location.state as any)?.from || '/dashboard'
  type LocationState = { from?: string }
  const state = location.state as LocationState | null
  const from = state?.from || '/dashboard'

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (isAuthenticated) {
    return <Navigate to={from} replace />
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo centrado */}
        <div className="flex justify-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-lg">
            <Shield className="h-10 w-10 text-white" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">eMozart</h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Plataforma de Assessment de Ciberseguridad
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg sm:rounded-lg sm:px-10">
          <Outlet />
        </div>

        <p className="mt-8 text-center text-xs text-gray-500">
          © {new Date().getFullYear()} eMozart. Basado en NIST CSF 2.0.
        </p>
      </div>
    </div>
  )
}

/**
 * Layout para páginas de error (404, 500, etc.)
 */
export const ErrorLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-lg">
            <Shield className="h-10 w-10 text-red-600" />
          </div>
        </div>
        {children}
      </div>
    </div>
  )
}
